import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Label,
} from "@/components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/ui/page-header";
import { iniciadorApi } from "@/services/api";
import {
  Link2,
  Loader2,
  CheckCircle,
  XCircle,
  Save,
  TestTube2,
  Eye,
  EyeOff,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

interface IniciadorConfig {
  id?: string;
  environment?: "SANDBOX" | "PRODUCTION";
  clientId?: string;
  clientSecretMasked?: string;
  dataBaseUrl?: string;
  linksBaseUrl?: string;
  redirectUrl?: string | null;
  defaultPermissions?: string[];
  webhookUrl?: string | null;
  isActive?: boolean;
  lastTestedAt?: string | null;
  lastTestStatus?: string | null;
  lastTestMessage?: string | null;
  createdAt?: string;
  updatedAt?: string;
  configured?: boolean;
}

const DEFAULT_PERMISSIONS = [
  "REGISTRATION_ALL",
  "ACCOUNTS_ALL",
];

const AVAILABLE_PERMISSIONS = [
  { value: "REGISTRATION_ALL", label: "Cadastro (todos)" },
  { value: "ACCOUNTS_ALL", label: "Contas (todos)" },
  { value: "CREDIT_CARDS_ALL", label: "Cartões de Crédito (todos)" },
  { value: "LOANS_ALL", label: "Empréstimos (todos)" },
  { value: "FINANCINGS_ALL", label: "Financiamentos (todos)" },
  { value: "INVOICE_FINANCINGS_ALL", label: "Antecipação de Recebíveis (todos)" },
];

export function IniciadorSettingsPage() {
  const [config, setConfig] = useState<IniciadorConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    environment: "SANDBOX" as "SANDBOX" | "PRODUCTION",
    clientId: "",
    clientSecret: "",
    dataBaseUrl: "https://data.sandbox.iniciador.com.br/v1/data",
    linksBaseUrl: "https://data.sandbox.iniciador.com.br/v1",
    redirectUrl: "",
    defaultPermissions: DEFAULT_PERMISSIONS,
    webhookUrl: "",
    webhookSecret: "",
  });

  // Result feedback
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  async function loadConfig() {
    try {
      const data = await iniciadorApi.getConfig();
      setConfig(data);

      // Populate form with existing data
      if (data.id) {
        setFormData({
          environment: data.environment || "SANDBOX",
          clientId: data.clientId || "",
          clientSecret: "", // Never returned from API
          dataBaseUrl: data.dataBaseUrl || "https://data.sandbox.iniciador.com.br/v1/data",
          linksBaseUrl: data.linksBaseUrl || "https://data.sandbox.iniciador.com.br/v1",
          redirectUrl: data.redirectUrl || "",
          defaultPermissions: data.defaultPermissions || DEFAULT_PERMISSIONS,
          webhookUrl: data.webhookUrl || "",
          webhookSecret: "", // Never returned from API
        });
      }
    } catch (error) {
      console.error("Erro ao carregar configuração:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleEnvironmentChange(value: "SANDBOX" | "PRODUCTION") {
    const urls = value === "PRODUCTION"
      ? {
          dataBaseUrl: "https://data.iniciador.com.br/v1/data",
          linksBaseUrl: "https://data.iniciador.com.br/v1",
        }
      : {
          dataBaseUrl: "https://data.sandbox.iniciador.com.br/v1/data",
          linksBaseUrl: "https://data.sandbox.iniciador.com.br/v1",
        };

    setFormData({
      ...formData,
      environment: value,
      ...urls,
    });
  }

  function togglePermission(permission: string) {
    const current = formData.defaultPermissions;
    const updated = current.includes(permission)
      ? current.filter(p => p !== permission)
      : [...current, permission];
    setFormData({ ...formData, defaultPermissions: updated });
  }

  async function handleSave() {
    setSaving(true);
    setResult(null);

    try {
      // Validate required fields for new config
      if (!config?.id) {
        if (!formData.clientId || !formData.clientSecret) {
          setResult({
            success: false,
            message: "Client ID e Client Secret são obrigatórios para criar a configuração",
          });
          setSaving(false);
          return;
        }
      }

      if (config?.id) {
        // Update existing config
        await iniciadorApi.updateConfig({
          environment: formData.environment,
          clientId: formData.clientId || undefined,
          clientSecret: formData.clientSecret || undefined,
          dataBaseUrl: formData.dataBaseUrl || undefined,
          linksBaseUrl: formData.linksBaseUrl || undefined,
          redirectUrl: formData.redirectUrl || undefined,
          defaultPermissions: formData.defaultPermissions,
          webhookUrl: formData.webhookUrl || undefined,
        });
      } else {
        // Create new config
        await iniciadorApi.createConfig({
          environment: formData.environment,
          clientId: formData.clientId,
          clientSecret: formData.clientSecret,
          dataBaseUrl: formData.dataBaseUrl || undefined,
          linksBaseUrl: formData.linksBaseUrl || undefined,
          redirectUrl: formData.redirectUrl || undefined,
          defaultPermissions: formData.defaultPermissions,
          webhookUrl: formData.webhookUrl || undefined,
        });
      }

      setResult({ success: true, message: "Configuração salva com sucesso!" });

      // Clear secret fields and reload
      setFormData(prev => ({ ...prev, clientSecret: "", webhookSecret: "" }));
      await loadConfig();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao salvar configuração",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setResult(null);

    try {
      const response = await iniciadorApi.testConnection();
      setResult({
        success: response.success,
        message: response.message,
      });
      await loadConfig(); // Reload to get updated test status
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao testar conexão",
      });
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Link2 className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Iniciador (Open Finance)"
          description="Configuração da integração com Open Finance via Iniciador"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Link2 className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Iniciador (Open Finance)"
        description="Configuração da integração com Open Finance via Iniciador"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testing || !config?.id || !config?.configured}
            >
              {testing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <TestTube2 className="w-4 h-4 mr-2" />
              )}
              Testar Conexão
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvar
            </Button>
          </div>
        }
      />

      {/* Status Card */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Link2 className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">Status da Integração</p>
                  <p className="text-sm text-muted-foreground">
                    {config?.id ? `Ambiente: ${config.environment}` : "Não configurado"}
                  </p>
                </div>
              </div>
              <Badge variant={config?.isActive ? "success" : "secondary"}>
                {config?.isActive ? "Ativo" : "Inativo"}
              </Badge>
            </div>
            {config?.lastTestedAt && (
              <div className="mt-3 text-sm text-muted-foreground">
                <p>Último teste: {formatDate(config.lastTestedAt)}</p>
                <p className={config.lastTestStatus === "success" ? "text-green-600" : "text-red-600"}>
                  {config.lastTestMessage}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">Credenciais</p>
                  <p className="text-sm text-muted-foreground">
                    {config?.configured ? "Client ID e Secret configurados" : "Pendente configuração"}
                  </p>
                </div>
              </div>
              <Badge variant={config?.configured ? "success" : "warning"}>
                {config?.configured ? "OK" : "Pendente"}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Result feedback */}
      {result && (
        <div
          className={`p-4 rounded-lg flex items-center gap-3 ${
            result.success
              ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {result.success ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{result.message}</span>
        </div>
      )}

      {/* Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configurações</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Environment */}
          <div className="space-y-2">
            <Label>Ambiente</Label>
            <Select
              value={formData.environment}
              onValueChange={handleEnvironmentChange}
            >
              <SelectTrigger className="w-full md:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SANDBOX">Sandbox (Testes)</SelectItem>
                <SelectItem value="PRODUCTION">Produção</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Use Sandbox para testes. Mude para Produção apenas após validar a integração.
            </p>
          </div>

          {/* Credentials */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Client ID</Label>
              <Input
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                placeholder="Seu Client ID do Iniciador"
              />
            </div>
            <div className="space-y-2">
              <Label>Client Secret</Label>
              <div className="relative">
                <Input
                  type={showSecret ? "text" : "password"}
                  value={formData.clientSecret}
                  onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                  placeholder={config?.configured ? "••••••••••••••••" : "Seu Client Secret"}
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {config?.configured && (
                <p className="text-xs text-muted-foreground">
                  Deixe em branco para manter o secret atual
                </p>
              )}
            </div>
          </div>

          {/* URLs */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>URL Base (Data)</Label>
              <Input
                value={formData.dataBaseUrl}
                onChange={(e) => setFormData({ ...formData, dataBaseUrl: e.target.value })}
                placeholder="https://data.sandbox.iniciador.com.br/v1/data"
              />
            </div>
            <div className="space-y-2">
              <Label>URL Base (Links)</Label>
              <Input
                value={formData.linksBaseUrl}
                onChange={(e) => setFormData({ ...formData, linksBaseUrl: e.target.value })}
                placeholder="https://data.sandbox.iniciador.com.br/v1"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>URL de Redirect (Callback)</Label>
            <Input
              value={formData.redirectUrl}
              onChange={(e) => setFormData({ ...formData, redirectUrl: e.target.value })}
              placeholder="https://app.stablebrld.com.br/open-finance/callback"
            />
            <p className="text-xs text-muted-foreground">
              URL para onde o usuário será redirecionado após autorizar o consentimento no banco
            </p>
          </div>

          {/* Permissions */}
          <div className="space-y-3">
            <Label>Permissões Padrão</Label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_PERMISSIONS.map((perm) => (
                <button
                  key={perm.value}
                  type="button"
                  onClick={() => togglePermission(perm.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    formData.defaultPermissions.includes(perm.value)
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {perm.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Permissões solicitadas ao criar novos links de consentimento
            </p>
          </div>

          {/* Webhook */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-sm font-semibold mb-4">Webhook (Opcional)</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>URL do Webhook</Label>
                <Input
                  value={formData.webhookUrl}
                  onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
                  placeholder="https://api.stablebrld.com.br/webhooks/iniciador"
                />
              </div>
              <div className="space-y-2">
                <Label>Secret do Webhook</Label>
                <Input
                  type="password"
                  value={formData.webhookSecret}
                  onChange={(e) => setFormData({ ...formData, webhookSecret: e.target.value })}
                  placeholder={config?.webhookUrl ? "••••••••••••••••" : "Secret para validar webhooks"}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Configure um webhook para receber notificações de mudança de status dos consentimentos
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
